import * as React from 'react';
import { CSSInterpolation } from '@emotion/serialize';
import {
  ToggleButton as MuiToggleButton,
  ToggleButtonProps as MuiToggleButtonProps,
} from '@material-ui/lab';
import { Theme } from '@emotion/react';
import { toggleButtonBase, toggleIconButtonBase } from 'bento/ToggleButton/toggleButtonCssDefs';
import { useEffect, useRef } from 'react';

interface OnSelectedProp {
  onSelected?: (selected: boolean, element: HTMLButtonElement | null) => void;
}

interface ToggleIconButtonProps extends MuiToggleButtonProps, OnSelectedProp {
  /**
   * Text label to describe the value represented by the icon
   */
  ['aria-label']: string;
  /**
   * @deprecated This property is baked in to our component and set to true intentionally.
   **/
  disableRipple?: MuiToggleButtonProps['disableRipple'];
}

interface ToggleButtonProps extends React.PropsWithChildren<MuiToggleButtonProps>, OnSelectedProp {
  css?: CSSInterpolation | ((theme: Theme) => CSSInterpolation);
  /**
   * @deprecated This property is baked in to our component and set to true intentionally.
   **/
  disableRipple?: MuiToggleButtonProps['disableRipple'];
}

const useOnSelected = (isSelected: boolean, onSelected: OnSelectedProp['onSelected']) => {
  const ref = useRef<HTMLButtonElement>() as React.RefObject<HTMLButtonElement>;

  useEffect(() => {
    onSelected?.(isSelected, ref.current);
  }, [onSelected, isSelected]);

  return { ref };
};

/**
 * Bamboo styled ToggleButton component to align with the secondary color theme
 * NOTE: ToggleButtons must be used inside of a ToggleButtonGroup or FormToggleButtonGroupField
 *
 * Demo:
 * [Storybook](http://tock-assets.tocktix.com/styleguide/dashboard/?path=/docs/components-togglebuttongroup--playground)
 *
 * Related Components: ui/ToggleButtonGroup, ui/FormToggleButtonGroupField
 * @deprecated Deprecated — use pantry SegmentedControl instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-segmentedcontrol--docs
 */
export const ToggleButton: React.FunctionComponent<ToggleButtonProps> = ({
  size,
  css,
  onSelected,
  selected,
  // NOTE: This has been pulled out of "...rest" to intentionally remove its use and disable it on all ToggleButton components.
  disableRipple,
  ...rest
}) => {
  const { ref } = useOnSelected(selected ?? false, onSelected);
  return (
    <MuiToggleButton
      css={[toggleButtonBase(size, !!selected), css]}
      size={size}
      ref={ref}
      selected={selected}
      disableRipple={true}
      {...rest}
    />
  );
};

/**
 * Specialized ToggleButton to be used with the Tock icon set.
 * This component strips the padding off our original components
 * NOTE: ToggleIconButtons must be used inside of a ToggleButtonGroup or FormToggleButtonGroupField
 *
 * Demo:
 * [Storybook](http://tock-assets.tocktix.com/styleguide/dashboard/?path=/docs/components-togglebuttongroup--playground)
 *
 * Related Components: ui/ToggleButtonGroup, ui/FormToggleButtonGroupField
 * @deprecated Deprecated — no pantry replacement available yet.
 * // TODO: verify pantry mapping
 * Migration: No direct pantry mapping — please reach out to the Design Systems team on Slack (e.g., #pantry-design-system) for assistance with a pantry replacement.
 */
export const ToggleIconButton: React.FunctionComponent<
  React.PropsWithChildren<ToggleIconButtonProps>
> = ({ onSelected, className, size, selected, disableRipple, ...rest }) => {
  const { ref } = useOnSelected(selected ?? false, onSelected);
  return (
    <MuiToggleButton
      className={className}
      css={[toggleButtonBase(size, !!selected), toggleIconButtonBase]}
      size={size}
      ref={ref}
      selected={selected}
      disableRipple={true}
      {...rest}
    />
  );
};
